// Admin-only CRUD for asset_types. New types appear in NewAssetForm's type picker in the same session
// (no deploy needed) — plan acceptance #1.

import React, { useState, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useAssetsStore from '../stores/useAssetsStore';
import { DataTable } from '../components/table/DataTable';
import { AssetType, AssetCategory, CATEGORY_LABELS } from '../Assets/types';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import { Checkbox } from '../components/ui/Checkbox';
import Reveal from '../components/ui/Reveal';
import { Field, FieldRow } from '../components/ui/Field';
import Select from '../components/ui/Select';

const DEFAULT_STATUSES = '["ready","flagged","ooc","checked_out","retired"]';

// Checkbox card used inside the New Asset Type modal. The whole card toggles; children render
// inside the card when checked (used for conditional follow-up inputs like ID Prefix).
const CheckboxCard: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description: string;
  children?: React.ReactNode;
}> = ({ checked, onChange, label, description, children }) => (
  <div
    onClick={() => onChange(!checked)}
    className={`p-3 rounded-lg ring-1 ring-inset transition-all duration-150 cursor-pointer ${
      checked
        ? 'bg-primary/5 ring-primary/30'
        : 'bg-gray-900/40 ring-gray-700/60 hover:ring-outline-variant'
    }`}
  >
    <Checkbox
      fill
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      label={<span className="font-medium">{label}</span>}
      description={description}
      labelProps={{ onClick: (e) => e.stopPropagation() }}
    />
    <Reveal show={!!(checked && children)}>
      <div className="mt-3 pl-[26px]" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </Reveal>
  </div>
);

// ---------------------------------------------------------------------------
// The type table: the shared DataTable's columns.
//
// Module scope, per the DataTable contract — the array never changes identity
// and no cell closes over page state. The one thing a cell needs that its own
// row cannot answer, what Deactivate does, arrives on the table's `meta` and is
// read at render time. Never memoize one of these cells: tanstack memoizes the
// cell context, so a React.memo'd cell would skip the parent's render and read
// stale meta (see web/docs/features/shared-table.md).
//
// No `sortable` and no accessorFn: the store hands these back in the order the
// API sends, which is the order NewAssetForm's picker shows them in, and a
// re-sortable column here would say the picker is in a different order.
// ---------------------------------------------------------------------------

interface AssetTypeTableMeta {
  handleToggleActive: (t: AssetType) => void;
}

const TYPE_COLUMNS: ColumnDef<AssetType, any>[] = [
  {
    id: 'key',
    header: 'Key',
    meta: { cellClass: 'font-mono text-on-surface' },
    cell: ({ row }) => row.original.key,
  },
  {
    id: 'label',
    header: 'Asset Type',
    meta: { cellClass: 'text-white' },
    cell: ({ row }) => row.original.label,
  },
  {
    id: 'category',
    header: 'Category',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => CATEGORY_LABELS[row.original.category],
  },
  {
    id: 'barcode_prefix',
    header: 'ID Prefix',
    meta: { cellClass: 'font-mono text-on-surface-variant' },
    cell: ({ row }) => row.original.barcode_prefix ?? '—',
  },
  // The three flag columns: a check or nothing at all. A blank cell is the
  // honest reading of "no" here — a second glyph for off would make every row
  // look like it carried three settings to compare.
  {
    id: 'auto_issue_display_code',
    header: 'Auto-issue',
    meta: { align: 'center', cellClass: 'text-xs' },
    cell: ({ row }) => (row.original.auto_issue_display_code ? '✓' : ''),
  },
  {
    id: 'is_built_in_house',
    header: 'In-house',
    meta: { align: 'center', cellClass: 'text-xs' },
    cell: ({ row }) => (row.original.is_built_in_house ? '✓' : ''),
  },
  {
    id: 'is_active',
    header: 'Active',
    meta: { align: 'center', cellClass: 'text-xs' },
    cell: ({ row }) => (row.original.is_active ? '✓' : ''),
  },
  {
    id: 'actions',
    header: 'Actions',
    meta: { align: 'right' },
    cell: (info) => {
      const { handleToggleActive } = info.table.options.meta as AssetTypeTableMeta;
      const t = info.row.original;
      return (
        <button
          onClick={() => handleToggleActive(t)}
          className={buttonClass('quiet', 'sm')}
        >
          {t.is_active ? 'Deactivate' : 'Reactivate'}
        </button>
      );
    },
  },
];

const AssetTypesAdmin: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const { types, refreshTypes } = useAssetsStore();

  const [showNew, setShowNew] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshTypes().catch(() => toast.error('Failed to load asset types')); }, []);

  const handleToggleActive = async (t: AssetType) => {
    try {
      await api.patch(`/asset-types/${t.id}`, { body: { is_active: !t.is_active } });
      await refreshTypes();
      toast.success(t.is_active ? 'Type deactivated' : 'Type reactivated');
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 space-y-5">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primary">Asset Types</h1>
        <button
          onClick={() => setShowNew(true)}
          className={buttonClass('filled')}
        >
          + New Type
        </button>
      </div>

      {/* The vessel is the shared table's: shell, radius, scroll container and
          the face on every row, so the page draws none of it.

          NO `loading` prop, deliberately: the store tracks no in-flight flag
          for this page to read, so an empty store and an unfinished fetch look
          the same and the empty line shows for both. That gap predates the move
          and is carried across rather than papered over with a spinner the page
          cannot actually time. */}
      <DataTable<AssetType>
        data={types}
        columns={TYPE_COLUMNS}
        getRowId={(t) => String(t.id)}
        meta={{ handleToggleActive }}
        empty={<EmptyState variant="area">No asset types yet</EmptyState>}
      />

      {showNew && (
        <NewTypeModal
          onClose={() => setShowNew(false)}
          onSuccess={async () => { setShowNew(false); await refreshTypes(); }}
        />
      )}
    </PageLayout>
  );
};

// --- New Type modal ---

const NewTypeModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const api = useApi();
  const toast = useToast();
  const [form, setForm] = useState({
    key: '',
    label: '',
    category: 'stable' as AssetCategory,
    barcode_prefix: '',
    auto_issue_display_code: true,
    is_built_in_house: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const opened = useRef(form);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      toast.error('Key and label are required');
      return;
    }
    if (form.auto_issue_display_code && !form.barcode_prefix.trim()) {
      toast.error('ID prefix is required when auto-issuing asset names');
      return;
    }
    setSubmitting(true);
    try {
      // requires_extension_table is intentionally hardcoded to false here — the admin
      // form no longer surfaces extension-table creation since the three Phase 1a
      // tables (smart_charger_details, time_machine_details, rtk_unit_details) cover
      // every dynamic type we need today. New extension tables require a DB migration
      // anyway, so they're handled out-of-band.
      const body: Record<string, unknown> = {
        key: form.key.trim().toLowerCase().replace(/\s+/g, '_'),
        label: form.label.trim(),
        category: form.category,
        auto_issue_display_code: form.auto_issue_display_code,
        is_built_in_house: form.is_built_in_house,
        requires_extension_table: false,
        operational_statuses: JSON.parse(DEFAULT_STATUSES),
      };
      if (form.barcode_prefix.trim()) body.barcode_prefix = form.barcode_prefix.trim().toUpperCase();

      await api.post('/asset-types', { body });
      toast.success(`Created type: ${form.label}`);
      onSuccess();
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 409) toast.error('A type with that key already exists');
      else toast.error(e.message || 'Failed to create type');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="New Asset Type"
      meta="Available in NewAssetForm immediately"
      closeHint="Close without saving"
      size="lg"
      busy={submitting}
      dirty={(Object.keys(form) as (keyof typeof form)[]).some(k => form[k] !== opened.current[k])}
      footer={
        <>
          <button onClick={onClose} className={buttonClass('quiet')}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={buttonClass('filled')}
          >
            {submitting ? 'Creating…' : 'Create Type'}
          </button>
        </>
      }
    >
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <FieldRow label="Key" hint="Internal identifier: lowercase with underscores.">
          <Field
            value={form.key}
            onChange={e => set('key', e.target.value)}
            placeholder="e.g. smoke_machine"
            className="font-mono"
          />
        </FieldRow>
        <FieldRow label="Asset Type" hint="Human-readable name operators will see.">
          <Field
            value={form.label}
            onChange={e => set('label', e.target.value)}
            placeholder="e.g. Smoke Machine"
          />
        </FieldRow>
      </div>

      <FieldRow label="Category">
        <Select
          label="Category"
          value={form.category}
          onChange={(k) => set('category', k as AssetCategory)}
          options={[
            { key: 'stable', label: 'Stable', hint: 'Uniform units, auto-issue names' },
            { key: 'dynamic', label: 'Dynamic', hint: 'Unique identities, may have extension fields' },
            { key: 'kit', label: 'Kit', hint: 'Composed of child assets' },
          ]}
        />
      </FieldRow>

      <div className="space-y-2.5 pt-1">
        <CheckboxCard
          checked={form.auto_issue_display_code}
          onChange={v => set('auto_issue_display_code', v)}
          label="Auto-issue asset names"
          description="Automatically number new assets: prefix + sequence. Uncheck if the user should name each one."
        >
          <FieldRow
            label="ID Prefix"
            hint={<>Leading letters in each name, e.g. <span className="font-mono">TARP</span> in <span className="font-mono">TARP0001</span>.</>}
          >
            <Field
              value={form.barcode_prefix}
              onChange={e => set('barcode_prefix', e.target.value.toUpperCase())}
              placeholder="TARP"
              className="font-mono"
            />
          </FieldRow>
        </CheckboxCard>

        <CheckboxCard
          checked={form.is_built_in_house}
          onChange={v => set('is_built_in_house', v)}
          label="Built in-house"
          description="Created in WIP inventory state. Check for gear Firefly assembles (SmartChargers, kits); leave unchecked for bought-in items (tarps, Pelican, Starlink)."
        />
      </div>
    </div>
    </Dialog>
  );
};

export default AssetTypesAdmin;
